import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, isStaff } from "@/context/AuthContext";
import { api, errMsg, RISK } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/layout/PublicLayout";
import { getReferral, clearReferral } from "@/pages/public/Referral";

const Shell = ({ title, sub, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden overflow-hidden bg-[#0B1B33] lg:block noise">
      <img src="/brand/hero_art.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33] via-transparent to-transparent" />
      <div className="absolute bottom-10 left-10 right-10 text-white">
        <p className="text-2xl italic">Plan the Launch. Respect the Landing.</p>
        <p className="mt-3 text-xs text-white/60">{RISK}</p>
      </div>
    </div>
    <div className="flex flex-col justify-center bg-white px-6 py-12 sm:px-16">
      <div className="mb-10 rounded-md bg-[#0B1B33] p-3 self-start"><Logo /></div>
      <h1 className="font-display text-5xl font-black uppercase">{title}</h1>
      {sub && <p className="mt-2 text-[#475569]">{sub}</p>}
      <div className="mt-8 max-w-md">{children}</div>
    </div>
  </div>
);

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-[#475569]">{label}</span>
    <Input className="mt-1" {...p} />
  </label>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "", code: "" });
  const [need2fa, setNeed2fa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(f.email, f.password, f.code);
      nav(sp.get("next") || (isStaff(u) ? "/superadmin" : "/app"));
    } catch (e2) {
      if (e2?.response?.data?.detail === "TWO_FACTOR_REQUIRED") { setNeed2fa(true); setErr("Enter the 6-digit code from your authenticator app."); } else setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Sign in" sub="Students and staff use the same sign-in.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <Field label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        {need2fa && <Field label="Two-step code" inputMode="numeric" autoComplete="one-time-code" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="login-2fa-input" />}
        {err && <p className="text-sm text-[#C8382E]" data-testid="login-error">{err}</p>}
        <button className="btn-gold w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Signing in…" : "Sign in"}</button>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="underline" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to={`/register${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="font-semibold underline" data-testid="register-link">Create account</Link>
        </div>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", marketing_consent: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const ref = getReferral();
      await register({ ...f, ref_click_id: ref?.click_id || "" });
      clearReferral();
      nav(sp.get("next") || "/app");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Create account" sub={getReferral() ? `${getReferral().first_name} invited you. Your welcome offer is applied at checkout.` : "Your student app holds tickets, receipts, quizzes and certificates."}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" />
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" />
        <Field label="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="register-phone-input" />
        <Field label="Password (8+ characters)" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" />
        <label className="flex items-start gap-2 text-xs text-[#475569]">
          <input type="checkbox" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} className="mt-0.5" data-testid="register-consent-checkbox" />
          Email me about new cities and free seminars (optional). I can unsubscribe at any time.
        </label>
        {err && <p className="text-sm text-[#C8382E]" data-testid="register-error">{err}</p>}
        <button className="btn-gold w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Creating…" : "Create account"}</button>
        <p className="text-sm">Already have an account? <Link to="/login" className="font-semibold underline">Sign in</Link></p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Shell title="Reset password" sub="We will email you a link to choose a new password.">
      {sent ? <p data-testid="forgot-sent">If an account exists for {email}, a reset link is on its way.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn-gold w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: sp.get("token"), password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Shell title="New password">
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        <button className="btn-gold w-full" data-testid="reset-submit-btn">Save password</button>
      </form>
    </Shell>
  );
}
